// Web-compatible wrapper for react-native-vector-icons
import React from 'react';
import { Text, StyleSheet, Platform } from 'react-native';

// Load MaterialIcons font for web
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  // Check if font is already loaded
  if (!document.getElementById('material-icons-font')) {
    const style = document.createElement('style');
    style.id = 'material-icons-font';
    // Use a CDN or local path - webpack will handle the font file
    style.textContent = `
      @font-face {
        font-family: 'MaterialIcons';
        src: url('https://fonts.gstatic.com/s/materialicons/v139/flUhRq6tzZclQEJ-Vdg-IuiaDsNc.woff2') format('woff2'),
             url('https://fonts.gstatic.com/s/materialicons/v139/flUhRq6tzZclQEJ-Vdg-IuiaDsNa.woff') format('woff');
        font-weight: normal;
        font-style: normal;
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
  }
}

// Icon component that renders Material Icons on web
const Icon = ({ name, size = 24, color = '#000', ...props }) => {
  if (Platform.OS === 'web') {
    // Use Unicode characters for Material Icons on web
    // This is a simplified approach - for production, consider using @expo/vector-icons
    // Comprehensive Material Icons map
    // Material Icons use Unicode Private Use Area (E000-F8FF)
    // This map includes all icons used in the app
    const iconMap = {
      // Navigation & Basic
      'home': '\ue88a',
      'explore': '\ue87a',
      'mail': '\ue158',
      'person': '\ue7fd',
      'arrow-back': '\ue5c4',
      'arrow-forward': '\ue5c8',
      'close': '\ue5cd',
      'check': '\ue5ca',
      'search': '\ue8b6',
      'menu': '\ue5d2',
      'more-vert': '\ue5d4',
      'add': '\ue145',
      'delete': '\ue872',
      'edit': '\ue254',
      'favorite': '\ue87d',
      'favorite-border': '\ue87e',
      'share': '\ue80d',
      'settings': '\ue8b8',
      'notifications': '\ue7f4',
      'notifications-none': '\ue7f5',
      'info': '\ue88e',
      'warning': '\ue002',
      'error': '\ue000',
      'check-circle': '\ue86c',
      'cancel': '\ue5c9',
      'save': '\ue161',
      'camera': '\ue3af',
      'photo': '\ue412',
      'image': '\ue3f4',
      'attachment': '\ue226',
      'send': '\ue163',
      'mic': '\ue029',
      'play-arrow': '\ue037',
      'pause': '\ue034',
      'stop': '\ue047',
      'skip-next': '\ue044',
      'skip-previous': '\ue045',
      'chevron-left': '\ue5cb',
      'chevron-right': '\ue5cc',
      'expand-more': '\ue5cf',
      'expand-less': '\ue5ce',
      'keyboard-arrow-down': '\ue313',
      'keyboard-arrow-up': '\ue316',
      'keyboard-arrow-left': '\ue314',
      'keyboard-arrow-right': '\ue315',
      
      // Icons from Smart Suggestions
      'trending-up': '\ue8e5',
      'local-hospital': '\ue548',
      'school': '\ue80c',
      'self-improvement': '\ue78d',
      
      // Benefits & Services
      'account-balance': '\ue84f',
      'work': '\ue8f1',
      'spa': '\ueb4c',
      'pregnant-woman': '\ue91e',
      
      // Communication
      'chat': '\ue0b7',
      'phone': '\ue0cd',
      
      // Journey & Family
      'child-care': '\ueb41',
      'family-restroom': '\uea15',
      'beach-access': '\ueb3e',
      
      // Actions & Status
      'auto-awesome': '\ue65f',
      'cloud-upload': '\ue2c3',
      'schedule': '\ue8b5',
      'star': '\ue838',
      'location-on': '\ue567',
      'verified': '\ue76e',
      'lightbulb': '\ue0ef',
      'label': '\ue892',
      
      // Medical & Health
      'medical-services': '\ue0f5',
      'task-alt': '\ue2e6',
      'card-giftcard': '\ue8f6',
      'list': '\ue896',
      'description': '\ue873',
      'privacy-tip': '\ue723',
      'help': '\ue887',
      'language': '\ue894',
      
      // Actions
      'download': '\ue2c4',
      'logout': '\ue879',
    };
    
    // If icon not found, try to convert name to Unicode
    // Material Icons use a specific encoding scheme
    let iconChar = iconMap[name];
    
    if (!iconChar) {
      // Try to find icon by converting name pattern
      // This is a fallback - ideally all icons should be in the map
      // For now, use a generic icon instead of home
      console.warn(`Icon "${name}" not found in icon map. Using info icon as fallback.`);
      iconChar = '\ue88e'; // Default to info icon (more neutral than home)
    }
    
    return (
      <Text
        {...props}
        style={[
          styles.icon,
          {
            fontSize: size,
            color: color,
            fontFamily: 'MaterialIcons',
          },
          props.style,
        ]}
      >
        {iconChar}
      </Text>
    );
  }
  
  // For native platforms, use the original library
  const VectorIcons = require('react-native-vector-icons/MaterialIcons');
  return <VectorIcons.default name={name} size={size} color={color} {...props} />;
};

const styles = StyleSheet.create({
  icon: {
    fontFamily: 'MaterialIcons',
    textAlign: 'center',
    includeFontPadding: false,
    textAlignVertical: 'center',
    // Web-specific styles
    ...(Platform.OS === 'web' && {
      display: 'inline-block',
      userSelect: 'none',
      speak: 'none',
      fontFeatureSettings: 'normal',
      fontVariationSettings: 'normal',
      textRendering: 'optimizeLegibility',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    }),
  },
});

export default Icon;

